/**
 * The creative preview.
 *
 * Follows the legacy renderer's rules, which are the specification: a snapshot beats the
 * raw asset, audio gets a player plus its artwork, video shows its thumbnail, and
 * POPUNDER / ADSIMILATE_EXTERNAL_LINK deliberately show nothing.
 *
 * ── Assets are remote, and that has a consequence ──
 *
 * Creatives are served from ad-serving CDNs, not from this origin. The API sets
 * `img-src 'self' data:`, so once the SPA is served by the API in production those
 * images are blocked. The CSP has to name the creative hosts before this works outside
 * the Vite dev server — recorded rather than discovered later against a wall of empty
 * boxes.
 *
 * Images are capped at 300px wide like the legacy, lazy-loaded because an ad group can
 * carry hundreds, and `referrerPolicy="no-referrer"` so browsing the ad list does not
 * leak internal URLs to third-party hosts.
 */

import { useState } from 'react';
import type { Ad } from '../lib/api';

export function Creative({ ad }: { ad: Ad }) {
  const [failed, setFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const c = ad.creative;

  // POPUNDER and ADSIMILATE_EXTERNAL_LINK, as in the legacy.
  if (!c) return <span className="no-preview">no preview</span>;

  if (c.kind === 'text') {
    // A text ad IS its copy — rendered as it appears, not as an asset.
    return (
      <div className="creative text-ad">
        <a className="t" href={ad.originalUrl || ad.destinationUrl} target="_blank" rel="noreferrer noopener">
          {ad.title || '(untitled)'}
        </a>
        <span className="u">{ad.displayUrl}</span>
        <p>{ad.description}</p>
      </div>
    );
  }

  // Video: the playable asset AND its static snapshot, side by side and labelled as such.
  if (c.kind === 'video') {
    if (!c.videoUrl && !c.imageUrl) return <span className="no-preview">no creative</span>;
    return (
      <div className="creative video-creative">
        {c.videoUrl && !videoFailed && (
          <figure className="cv-item">
            <video
              className="video" controls preload="metadata"
              src={c.videoUrl} poster={c.imageUrl ?? undefined}
              onError={() => setVideoFailed(true)}
            >
              Your browser does not support video playback.
            </video>
            <figcaption className="cv-label">Video (playable)</figcaption>
          </figure>
        )}
        {c.videoUrl && videoFailed && (
          <span className="no-preview broken" title={c.videoUrl}>video did not load</span>
        )}
        {c.imageUrl && (
          <figure className="cv-item">
            {!failed
              ? <img src={c.imageUrl} alt={`${ad.type} snapshot`} loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
              : <span className="no-preview broken" title={c.imageUrl}>snapshot did not load</span>}
            <figcaption className="cv-label">Static snapshot</figcaption>
          </figure>
        )}
      </div>
    );
  }

  if (c.kind === 'none' || (!c.imageUrl && !c.audioUrl && !c.videoUrl)) {
    return <span className="no-preview">no creative</span>;
  }

  return (
    <div className="creative">
      {c.audioUrl && (
        <audio controls preload="none" src={c.audioUrl} className="audio">
          Your browser does not support audio playback.
        </audio>
      )}
      {c.imageUrl && !failed && (
        <img
          src={c.imageUrl}
          alt={`${ad.type} creative`}
          title={c.width && c.height ? `${c.width}×${c.height}` : undefined}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      )}
      {c.imageUrl && failed && (
        /*
         * An asset that will not load says so. A blank cell would read as "this ad has no
         * creative", which is a different and misleading fact — and with a CSP in play it
         * is the likely failure.
         */
        <span className="no-preview broken" title={c.imageUrl}>asset did not load</span>
      )}
      {c.width && c.height && <span className="dims">{c.width}×{c.height}</span>}
    </div>
  );
}
